import type { System } from "../types";
import { migrate } from "../schema/migrate";
import { SystemFileError } from "../persistence";
import { getSupabase } from "./client";
import {
  captureAnalytics,
  track,
  type CloudProjectAccessMode,
  type CloudProjectCreationSource,
} from "../analytics";

export type ProjectRow = {
  id: string;
  name: string;
  updated_at: string;
  created_at: string;
};

export type ProjectFull = ProjectRow & { data: System };

type ProjectAccessContext = {
  claimed_free_slot: boolean;
  free_slot_claim_number: number | null;
  free_slot_claim_reason: "backfill" | "create" | "update_reclaim" | null;
  has_lifetime_access: boolean;
  has_legacy_access: boolean;
  monetization_enabled: boolean;
};

type CreatedProjectWithContext = ProjectRow & ProjectAccessContext;

function cloudProjectAccessMode(
  context: ProjectAccessContext,
): CloudProjectAccessMode {
  if (!context.monetization_enabled) return "unmetered";
  if (context.has_lifetime_access) return "lifetime";
  if (context.has_legacy_access) return "legacy";
  return "free";
}

function captureProjectCreated(
  project: CreatedProjectWithContext,
  system: System,
  source: CloudProjectCreationSource,
): void {
  const accessMode = cloudProjectAccessMode(project);
  captureAnalytics("project_created", {
    project_id: project.id,
    node_count: system.nodes.length,
    cable_count: system.cables.length,
    source,
    access_mode: accessMode,
    claimed_free_slot: project.claimed_free_slot,
    free_slot_claim_number: project.free_slot_claim_number,
    free_slot_claim_reason: project.free_slot_claim_reason,
    free_slot_recreated:
      project.claimed_free_slot &&
      (project.free_slot_claim_number ?? 0) > 1,
    has_lifetime_access: project.has_lifetime_access,
    has_legacy_access: project.has_legacy_access,
    monetization_enabled: project.monetization_enabled,
  });
}

export type ShareRole = "viewer" | "commenter" | "editor";
export type AccessRole = "owner" | ShareRole;

export type ShareRow = {
  project_id: string;
  role: ShareRole;
  token: string;
  created_at: string;
};

export type CommentRow = {
  id: string;
  project_id: string;
  node_id: string | null;
  cable_id: string | null;
  body: string;
  author_id: string | null;
  author_name: string;
  created_at: string;
  resolved_at: string | null;
  pinned_at: string | null;
};

export type LoadedProject = {
  id: string;
  name: string;
  updated_at: string;
  system: System;
  /** When loaded via share token, the role granted by that token. */
  role: AccessRole;
  /** The token used (for token-protected updates). null for owner loads. */
  token: string | null;
  isPublic?: boolean;
  /** Filled on public loads, for attribution. */
  ownerId?: string;
  /** Display name from auth.users metadata. */
  ownerName?: string;
  description?: string | null;
  /** URL slug for /p/<slug> public links. */
  slug?: string | null;
  /** Published anonymously: owner name and id are hidden. */
  isAnonymous?: boolean;
};

function resolvedProjectName(projectName: string, systemName: unknown): string {
  if (typeof systemName !== "string") return projectName;
  const isDefaultName = (name: string) => /^(?:untitled(?: system)?)?$/i.test(name.trim());
  return isDefaultName(systemName) && !isDefaultName(projectName)
    ? projectName
    : systemName;
}

function migrateOrThrow(raw: unknown, projectName: string): System {
  const result = migrate(raw);
  if (!result.ok) {
    throw new SystemFileError(result.error);
  }
  // Older picker renames updated only projects.name, while toolbar renames
  // updated only data.name. Recover an untouched default without discarding a
  // meaningful toolbar name when both historic values differ.
  return { ...result.system, name: resolvedProjectName(projectName, result.system.name) };
}

/* ───── Owner-scoped (RLS by auth.uid) ───── */

export async function listProjects(ownerId: string): Promise<ProjectRow[]> {
  const sb = getSupabase();
  const { data, error } = await sb
    .from("projects")
    .select("id, name, system_name:data->>name, updated_at, created_at")
    .eq("owner_id", ownerId)
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map(({ system_name, ...row }) => ({
    ...row,
    name: resolvedProjectName(row.name, system_name),
  }));
}

export async function loadProject(
  id: string,
  ownerId: string,
): Promise<LoadedProject> {
  const sb = getSupabase();
  const { data, error } = await sb
    .from("projects")
    .select("id, name, updated_at, data")
    .eq("id", id)
    .eq("owner_id", ownerId)
    .single();
  if (error) throw error;
  const system = migrateOrThrow(data.data, data.name);
  return {
    id: data.id,
    name: system.name,
    updated_at: data.updated_at,
    system,
    role: "owner",
    token: null,
  };
}

export async function createProject(
  name: string,
  system: System,
  source: CloudProjectCreationSource,
): Promise<ProjectRow> {
  const sb = getSupabase();
  const { data, error } = await sb.rpc("create_cloud_project_with_context", {
    p_name: name,
    p_data: { ...system, name },
  });
  if (error) {
    throw error;
  }
  const row = (Array.isArray(data) ? data[0] : data) as
    | CreatedProjectWithContext
    | null;
  if (!row) throw new Error("Cloud project creation returned no project");
  captureProjectCreated(row, system, source);
  return row;
}

export async function saveProject(
  id: string,
  system: System,
  expectedUpdatedAt: string,
): Promise<string> {
  const sb = getSupabase();
  const { data, error } = await sb.rpc("save_project_with_name_if_current", {
    p_project_id: id,
    p_data: system,
    p_expected_updated_at: expectedUpdatedAt,
    p_name: system.name,
  });
  if (error) throw error;
  return data as string;
}

export async function renameProject(
  id: string,
  name: string,
  expectedUpdatedAt: string,
): Promise<void> {
  const sb = getSupabase();
  const { data, error } = await sb
    .from("projects")
    .update({ name })
    .eq("id", id)
    .eq("updated_at", expectedUpdatedAt)
    .select("id")
    .maybeSingle();
  if (error) throw error;
  if (!data) {
    throw Object.assign(new Error("Project changed. Reopen Projects and try again."), {
      code: "40001",
    });
  }
}

export async function duplicateProject(
  id: string,
  newName: string,
  source: CloudProjectCreationSource,
  ownerId: string,
): Promise<ProjectRow> {
  const src = await loadProject(id, ownerId);
  return createProject(newName, src.system, source);
}

export async function deleteProject(
  id: string,
  context: {
    source: "desktop_project_picker";
    wasCurrentProject: boolean;
  },
): Promise<void> {
  const sb = getSupabase();
  const { data, error } = await sb.rpc("delete_cloud_project_with_context", {
    p_project_id: id,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row?.deleted) throw new Error("Cloud project deletion was not confirmed");
  captureAnalytics("cloud_project_deleted", {
    project_id: row.project_id,
    source: context.source,
    was_current_project: context.wasCurrentProject,
    released_free_slot: Boolean(row.released_free_slot),
    free_slot_claim_number: row.free_slot_claim_number ?? null,
    has_lifetime_access: Boolean(row.has_lifetime_access),
    has_legacy_access: Boolean(row.has_legacy_access),
    monetization_enabled: Boolean(row.monetization_enabled),
  });
}

/* ───── Admin (cross-user, allowlist-gated) ───── */

/** The email allowlist lives in migration 20260519100000_admin_cross_user_project_access.sql. */
export type AdminProjectRow = ProjectRow & {
  owner_id: string;
  owner_email: string | null;
  owner_name: string | null;
  is_public: boolean;
};

export { isCurrentUserAdmin } from "./admin";

export async function adminListAllProjects(): Promise<AdminProjectRow[]> {
  const sb = getSupabase();
  const { data, error } = await sb.rpc("admin_list_all_projects");
  if (error) throw error;
  return (data ?? []) as AdminProjectRow[];
}

export async function adminLoadProject(id: string): Promise<LoadedProject & {
  ownerEmail: string | null;
}> {
  const sb = getSupabase();
  const { data, error } = await sb.rpc("admin_load_project", { p_id: id });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error("Project not found.");
  const system = migrateOrThrow(row.data, row.name);
  return {
    id: row.id,
    name: system.name,
    updated_at: row.updated_at,
    system,
    role: "viewer",
    token: null,
    ownerId: row.owner_id,
    ownerName: row.owner_name ?? null,
    ownerEmail: row.owner_email ?? null,
  };
}

/* ───── Shares (owner-only RPCs) ───── */

export async function listShares(projectId: string): Promise<ShareRow[]> {
  const sb = getSupabase();
  const { data, error } = await sb.rpc("list_shares", { p_project_id: projectId });
  if (error) throw error;
  return (data ?? []) as ShareRow[];
}

export async function mintShare(projectId: string, role: ShareRole): Promise<string> {
  const sb = getSupabase();
  const { data, error } = await sb.rpc("mint_share", {
    p_project_id: projectId,
    p_role: role,
  });
  if (error) throw error;
  track("share_link_minted", { project_id: projectId, role });
  return data as string;
}

/**
 * `viewer` deliberately: a forwarded link reads the schematic, never edits it.
 * Requires a session — `createProject` throws without one.
 */
export async function shareLocalSystem(
  name: string,
  system: System,
): Promise<{ project: ProjectRow; token: string }> {
  const sb = getSupabase();
  const { data, error } = await sb.rpc(
    "create_project_and_mint_share_with_context",
    {
      p_name: name,
      p_data: { ...system, name },
      p_role: "viewer",
    },
  );
  if (error) {
    throw error;
  }
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error("Cloud share creation returned no project");
  const project: ProjectRow = {
    id: row.project_id,
    name: row.project_name,
    updated_at: row.project_updated_at,
    created_at: row.project_created_at,
  };
  captureProjectCreated(
    {
      ...project,
      claimed_free_slot: Boolean(row.claimed_free_slot),
      free_slot_claim_number: row.free_slot_claim_number ?? null,
      free_slot_claim_reason: row.free_slot_claim_reason ?? null,
      has_lifetime_access: Boolean(row.has_lifetime_access),
      has_legacy_access: Boolean(row.has_legacy_access),
      monetization_enabled: Boolean(row.monetization_enabled),
    },
    system,
    "mobile_share",
  );
  track("share_link_minted", { project_id: project.id, role: "viewer" });
  return { project, token: row.share_token };
}

export async function revokeShare(projectId: string, role: ShareRole): Promise<void> {
  const sb = getSupabase();
  const { error } = await sb.rpc("revoke_share", { p_project_id: projectId, p_role: role });
  if (error) throw error;
}

/* ───── Public / community ───── */

export type PublishStatus = {
  isPublic: boolean;
  description: string | null;
  publishedAt: string | null;
  slug: string | null;
  isAnonymous: boolean;
};

export async function getPublishStatus(projectId: string): Promise<PublishStatus> {
  const sb = getSupabase();
  const { data, error } = await sb
    .from("projects")
    .select("is_public, description, published_at, slug, is_anonymous")
    .eq("id", projectId)
    .single();
  if (error) throw error;
  return {
    isPublic: Boolean(data.is_public),
    description: data.description ?? null,
    publishedAt: data.published_at ?? null,
    slug: data.slug ?? null,
    isAnonymous: Boolean(data.is_anonymous),
  };
}

export async function setProjectPublic(
  projectId: string,
  isPublic: boolean,
  description?: string | null,
  isAnonymous?: boolean | null,
): Promise<void> {
  const sb = getSupabase();
  const { error } = await sb.rpc("set_project_public", {
    p_project_id: projectId,
    p_public: isPublic,
    p_description: description ?? null,
    p_anonymous: isAnonymous ?? null,
  });
  if (error) throw error;
  track(isPublic ? "project_published" : "project_unpublished", {
    project_id: projectId,
    anonymous: isAnonymous ?? null,
  });
}

export async function loadPublicProject(idOrSlug: string): Promise<LoadedProject> {
  const sb = getSupabase();
  const { data, error } = await sb.rpc("get_public_project", { p_id_or_slug: idOrSlug });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error("This project isn't public, or the link is wrong.");
  const system = migrateOrThrow(row.data, row.name);
  return {
    id: row.id,
    name: system.name,
    updated_at: row.updated_at,
    system,
    role: "viewer",
    token: null,
    isPublic: true,
    ownerId: row.owner_id,
    description: row.description ?? null,
    slug: row.slug ?? null,
    isAnonymous: Boolean(row.is_anonymous),
  };
}

/** Lightweight meta (no `data` jsonb) for OG / preview surfaces. */
export type PublicProjectMeta = {
  id: string;
  slug: string | null;
  name: string;
  description: string | null;
  owner_name: string;
  published_at: string | null;
  is_anonymous: boolean;
};

export async function getPublicProjectMeta(idOrSlug: string): Promise<PublicProjectMeta | null> {
  const sb = getSupabase();
  const { data, error } = await sb.rpc("get_public_project_meta", { p_id_or_slug: idOrSlug });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return (row as PublicProjectMeta | null) ?? null;
}

/* ───── Demo share (anon-callable) ───── */

export type DemoShare = { token: string; projectId: string; name: string };

export async function getDemoShare(): Promise<DemoShare> {
  const sb = getSupabase();
  const { data, error } = await sb.rpc("get_demo_share");
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error("Demo project is not configured.");
  return { token: row.token, projectId: row.project_id, name: row.name };
}

/* ───── Token-protected (anonymous share-link access) ───── */

export async function loadByToken(token: string): Promise<LoadedProject> {
  const sb = getSupabase();
  const { data, error } = await sb.rpc("get_project_by_token", { p_token: token });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error("Share link is invalid or revoked.");
  const system = migrateOrThrow(row.data, row.name);
  return {
    id: row.id,
    name: system.name,
    updated_at: row.updated_at,
    system,
    role: row.role as ShareRole,
    token,
  };
}

export async function saveByToken(
  token: string,
  system: System,
  expectedUpdatedAt: string,
): Promise<string> {
  const sb = getSupabase();
  const { data, error } = await sb.rpc("update_project_by_token_with_name_if_current", {
    p_token: token,
    p_data: system,
    p_expected_updated_at: expectedUpdatedAt,
    p_name: system.name,
  });
  if (error) throw error;
  return data as string;
}

/* ───── Comments ───── */

export type CommentTarget = { nodeId: string } | { cableId: string };

export async function listComments(
  projectId: string,
  access: { token: string | null },
): Promise<CommentRow[]> {
  const sb = getSupabase();
  if (access.token) {
    const { data, error } = await sb.rpc("list_comments_by_token", { p_token: access.token });
    if (error) throw error;
    return (data ?? []) as CommentRow[];
  }
  const { data, error } = await sb
    .from("comments")
    .select("*")
    .eq("project_id", projectId)
    .order("created_at");
  if (error) throw error;
  return data ?? [];
}

export async function addComment(
  projectId: string,
  target: CommentTarget,
  body: string,
  authorName: string,
  access: { token: string | null },
): Promise<CommentRow> {
  const sb = getSupabase();
  const node_id = "nodeId" in target ? target.nodeId : null;
  const cable_id = "cableId" in target ? target.cableId : null;
  if (access.token) {
    const { data, error } = await sb.rpc("create_comment_by_token", {
      p_token: access.token,
      p_node_id: node_id,
      p_cable_id: cable_id,
      p_body: body,
      p_author_name: authorName,
    });
    if (error) throw error;
    track("comment_added", {
      project_id: projectId,
      target: cable_id ? "cable" : node_id ? "node" : "project",
      via_token: true,
    });
    return data as CommentRow;
  }
  const { data: user } = await sb.auth.getUser();
  const { data, error } = await sb
    .from("comments")
    .insert({
      project_id: projectId,
      node_id,
      cable_id,
      body,
      author_id: user.user?.id ?? null,
      author_name: authorName,
    })
    .select("*")
    .single();
  if (error) throw error;
  track("comment_added", {
    project_id: projectId,
    target: cable_id ? "cable" : node_id ? "node" : "project",
    via_token: false,
  });
  return data;
}

export async function resolveComment(
  commentId: string,
  access: { token: string | null },
): Promise<void> {
  const sb = getSupabase();
  if (access.token) {
    const { error } = await sb.rpc("resolve_comment_by_token", {
      p_token: access.token,
      p_comment_id: commentId,
    });
    if (error) throw error;
    return;
  }
  const { error } = await sb
    .from("comments")
    .update({ resolved_at: new Date().toISOString() })
    .eq("id", commentId);
  if (error) throw error;
}

/**
 * Owner-only. The RPC clears any prior pin on the same target, so only one
 * comment stays elevated; returns the new pinned state.
 */
export async function togglePinComment(commentId: string): Promise<boolean> {
  const sb = getSupabase();
  const { data, error } = await sb.rpc("toggle_pin_comment", {
    p_comment_id: commentId,
  });
  if (error) throw error;
  return Boolean(data);
}
